import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { State } from '../../shared/core/state';
import { CourseItem } from '../../shared/core/state/course/models';
import * as CourseActions from '../../shared/core/state/course/course.actions';
import * as OrderActions from '../../shared/core/state/order/order.actions';
import { CreateOrderDTO } from '../../shared/core/state/order/order.model';
import { OrderService } from '../../shared/core/state/order/order.service';

@Component({
  selector: 'black-course-enroll',
  templateUrl: './course-enroll.component.html',
  styleUrls: ['./course-enroll.component.css'],
  standalone: false,
  host: {
    ngSkipHydration: 'true',
  },
})
export class CourseEnrollComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private store = inject<Store<State>>(Store);
  private orderService = inject(OrderService);
  private destroy$ = new Subject<void>();

  selectedCourse: CourseItem | null = null;
  selectedPlan: 'FULL' | 'INSTALLMENT' = 'FULL';
  isSubmitting = false;

  get isEnrolled(): boolean {
    return Boolean(
      this.selectedCourse?.enrollments &&
        this.selectedCourse.enrollments.length > 0
    );
  }

  get price(): number {
    return this.selectedCourse?.settings?.price ?? 0;
  }

  get priceFormatted(): string {
    if (this.price === 0) return 'Free';
    return `LKR ${this.price.toLocaleString('en-LK')}`;
  }

  get hasInstallmentOptions(): boolean {
    const opts = this.selectedCourse?.settings?.installmentOptions;
    return Boolean(
      opts &&
        opts.enabled &&
        opts.numberOfInstallments > 1 &&
        this.price > 0
    );
  }

  get installmentCount(): number {
    return this.selectedCourse?.settings?.installmentOptions?.numberOfInstallments || 2;
  }

  get installmentIntervalDays(): number {
    return this.selectedCourse?.settings?.installmentOptions?.intervalDays || 30;
  }

  get installmentBaseAmount(): number {
    if (!this.hasInstallmentOptions) return 0;
    return Math.floor(this.price / this.installmentCount);
  }

  get firstInstallmentAmount(): number {
    if (!this.hasInstallmentOptions) return 0;
    return this.price - this.installmentBaseAmount * (this.installmentCount - 1);
  }

  get installmentSchedule(): Array<{ number: number; amount: number; dueDate: Date }> {
    if (!this.hasInstallmentOptions) return [];
    const now = Date.now();
    const dayMs = this.installmentIntervalDays * 86_400_000;
    return Array.from({ length: this.installmentCount }, (_, i) => ({
      number: i + 1,
      amount: i === 0 ? this.firstInstallmentAmount : this.installmentBaseAmount,
      dueDate: new Date(now + i * dayMs),
    }));
  }

  ngOnInit(): void {
    const courseId = this.route.snapshot.paramMap.get('id');
    if (courseId) {
      this.store.dispatch(CourseActions.selectCourseItem({ id: courseId }));

      this.orderService
        .getPendingCourseOrders()
        .pipe(takeUntil(this.destroy$))
        .subscribe((map) => {
          const pending = map.get(courseId);
          if (pending) {
            if (pending.status === 'AWAITING_APPROVAL') {
              this.router.navigate(['/dashboard/orders']);
            } else {
              this.router.navigate(['/order/payment'], {
                queryParams: { orderId: pending.orderId },
              });
            }
          }
        });
    } else {
      this.router.navigate(['/courses']);
    }

    this.store
      .select((state) => state.courses.selectedCourseItem)
      .pipe(takeUntil(this.destroy$))
      .subscribe((course) => {
        if (course) {
          this.selectedCourse = course;
          if (
            course.settings?.price === 0 ||
            (course.enrollments && course.enrollments.length > 0)
          ) {
            // Free course or already enrolled: direct learning
            this.router.navigate(['/courses', course._id, 'playlist']);
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSelectPlan(plan: 'FULL' | 'INSTALLMENT'): void {
    this.selectedPlan = plan;
  }

  proceedToPayment(): void {
    if (!this.selectedCourse || this.isSubmitting) return;

    this.isSubmitting = true;
    const order: CreateOrderDTO = {
      items: [this.selectedCourse._id],
      paymentPlan: this.selectedPlan,
      currency: 'LKR',
    };

    this.orderService
      .create(order)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (newOrder) => {
          this.isSubmitting = false;
          if (newOrder) {
            this.store.dispatch(OrderActions.placeOrderSuccess({ response: newOrder }));
            const orderId = newOrder.orderId || newOrder._id;
            this.router.navigate(['/order/payment'], {
              queryParams: { orderId },
            });
          }
        },
        error: (err) => {
          this.isSubmitting = false;
          this.store.dispatch(OrderActions.placeOrderFailed({ err }));
        },
      });
  }

  goToLearning(): void {
    if (this.selectedCourse) {
      this.router.navigate(['/courses', this.selectedCourse._id, 'playlist']);
    }
  }
}
